import React, { useContext, useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useOktaAuth } from '@okta/okta-react';

import { apiEndPoints } from '../../common/api.endPoints';
import { Body, Spacer } from '../../common/components/v2';
import { ConfigService } from '../../common/config.service';
import { Roles } from '../../common/constants/roles';
import { UserOrganization } from '../../common/constants/userOrganization';
import { clientEnvVars } from '../../common/environment/clientEnvVars';
import { FeatureFlagsContext } from '../../common/featureFlagsWrapper';
import { FetchService } from '../../common/fetch.service';
import { HttpService } from '../../common/http.service';
import styles from './styles.module.scss';
import { TncModal } from './TncModal';

interface IAuthContext {
  prioritizedView?: boolean;
  toggleView?: () => void;
  setPrioritizedView?: React.Dispatch<React.SetStateAction<boolean>>;
  id: number | null;
  userName: string;
  loggedIn: boolean;
  roles: string[];
  skills?: string[];
  userOrganization: string;

  /** RBAC v2 permissions. Populated when isNewRbacEnabled is on. */
  permissions?: Set<string>;
}

export const AuthContext = React.createContext<IAuthContext>({
  id: null as number | null,
  userName: '',
  loggedIn: false,
  roles: [] as string[],
  skills: [] as string[],
  userOrganization: '',
  permissions: undefined,
  prioritizedView: undefined,
});

/**
 * Auth wrapper is a component which takes care of calling createUser endpoint
 * on the backend before any route gets a chance to render.
 * @param props
 */
export const AuthWrapper = (props: any) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [prioritizedView, setPrioritizedView] = useState(false);
  const { flags } = useContext(FeatureFlagsContext);
  const [userCreation, setUserCreation] = useState({
    done: false,
    error: false,
    id: null,
    userName: '',
    roles: [] as string[],
    skills: [] as string[],
    acceptedTnc: false,
    userOrganization: '',
    permissions: undefined as Set<string> | undefined,
  });

  useEffect(() => {
    if (!authState?.isAuthenticated) return;

    // Right now, I haven't found a better way to deal with token expiration.
    // authState.isAuthenticated only checks the presence of accessToken and idToken but does not check the expiration
    // refer https://github.com/okta/okta-auth-js#authstatemanager
    const expiresAt = authState.accessToken?.expiresAt ?? 0;
    const now = Math.floor(Date.now() / 1000);
    if (expiresAt < now) {
      (async () => {
        await oktaAuth.signOut({
          postLogoutNavigateUri: ConfigService.getInstance().getLogoutUrl(),
        });
      })();
      return;
    }

    (async () => {
      const httpService = HttpService.getInstance();
      httpService.setOktaAuthService(oktaAuth);

      const fetchService = FetchService.getInstance();
      fetchService.setOktaAuthService(oktaAuth);

      // **Modified line: Added an empty string as the body parameter**
      const response = await httpService.post(apiEndPoints.createUser, '');

      const permissionsList = response.parsedBody?.permissions as
        | string[]
        | undefined;
      const permissions =
        Array.isArray(permissionsList) && permissionsList.length > 0
          ? new Set(permissionsList)
          : undefined;

      const userData = {
        done: true,
        error: !response || response.error,
        id: response.parsedBody?.id || null,
        userName: response.parsedBody?.displayName || '',
        roles: response.parsedBody?.roles || [],
        skills: response.parsedBody?.skills || [],
        acceptedTnc: response.parsedBody?.acceptedTnc || false,
        userOrganization: response.parsedBody?.userOrganization || '',
        permissions,
      };

      setUserCreation(userData);

      // Set Datadog RUM user context for session tracking
      if (userData.id && userData.userName) {
        datadogRum.setUser({
          id: userData.id.toString(),
          name: userData.userName,
          email: response.parsedBody?.email || '',
        });
      }

      if (!response || response.error) {
        oktaAuth.signOut({
          postLogoutNavigateUri: ConfigService.getInstance().getLogoutUrl(),
        });
      }
    })();
  }, [authState]);

  useEffect(() => {
    setPrioritizedView(
      getIsPrioritizedDefault(
        userCreation.roles,
        userCreation.userOrganization,
        flags.queue_prioritization,
      ),
    );
  }, [
    userCreation.roles,
    userCreation.userOrganization,
    flags.queue_prioritization,
  ]);

  if (userCreation.error) {
    return (
      <div className={styles.Error}>
        <Body>
          You may not have permissions to access this application. Please try to
          log out and log in again. If the problem persists, please contact your
          administrator.
        </Body>
        <Spacer height={32} />
        <a
          href="#"
          onClick={evt => {
            evt.preventDefault();
            oktaAuth.signOut({
              postLogoutNavigateUri: ConfigService.getInstance().getLogoutUrl(),
            });
          }}
        >
          Log out
        </a>
      </div>
    );
  }

  if (userCreation.done && !userCreation.acceptedTnc) {
    return (
      <TncModal
        onAccepted={error => {
          if (error) {
            setUserCreation({ ...userCreation, error: true });
          } else {
            setUserCreation({ ...userCreation, acceptedTnc: true });
          }
        }}
      ></TncModal>
    );
  }
  const toggleView = () => {
    const newUrl = `/${
      clientEnvVars().NORA_SUBDIR
    }/?prioritizedview=${!prioritizedView}`;
    window.location.href = newUrl;
  };

  return (
    <AuthContext.Provider
      value={{
        id: userCreation.id,
        userName: userCreation.userName,
        loggedIn: userCreation.done && !userCreation.error,
        roles: userCreation.roles,
        skills: userCreation.skills,
        userOrganization: userCreation.userOrganization,
        permissions: userCreation.permissions,
        prioritizedView,
        toggleView,
        setPrioritizedView,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

const prioritizedViewRoles = [
  // v1 defaults
  Roles.UNDERWRITER,
  Roles.UW_FLEX_UNDERWRITER,
  Roles.PRODUCTION_UNDERWRITER,
  Roles.UW_UNDERWRITING_MANAGER,
  // v2 defaults (Underwriters, Admin, Senior Ops Underwriter)
  Roles.WORKBENCH_UNDERWRITER,
  Roles.WORKBENCH_ADMIN,
  Roles.WORKBENCH_SENIOR_OPS_UNDERWRITER,
];

const getIsPrioritizedDefault = (
  roles: string[],
  userOrganization: string,
  isEnabled: boolean,
) => {
  if (userOrganization !== UserOrganization.ETHOS || !isEnabled) return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has('prioritizedView'))
    return params.get('prioritizedView') === 'true';
  return roles.some(role => prioritizedViewRoles.includes(role));
};
